'use client';

import {NAV_OWNER} from '@/constants';
import {useCountry} from '@/hooks';
import {useGlobalClickRouteHandler} from '@/lib/routing';
import {emitEvent} from '@/lib/utils/analytics';
import {genRoute} from '@/lib/utils/route.utils';
import {locales} from '@i18n/config';
import {FooterLinkClickEvent} from '@wf-components/footer-consumer';
import {EgsFooter} from '@wf-components/footer-react';
import {useRouter} from 'next/navigation';
import {useCallback} from 'react';

interface Props {
	locale: string;
}

function isFooterLink(
	e: CustomEvent<FooterLinkClickEvent>
): e is CustomEvent<FooterLinkClickEvent> {
	const event = e;
	return Boolean(event.detail.link && event.detail.link?.label);
}

export const GlobalFooterWrapper = ({locale}: Props) => {
	const onGlobalClick = useGlobalClickRouteHandler<FooterLinkClickEvent>(locales, NAV_OWNER);
	const router = useRouter();
	const country = useCountry();

	const serverUrl = process.env.NEXT_PUBLIC_EPIC_SERVER_URL;

	const footerLinkOnClick = useCallback(
		(e: CustomEvent<FooterLinkClickEvent>) => {
			const link = e.detail?.link;
			const linkEvent: CustomEvent<FooterLinkClickEvent> | undefined = isFooterLink(e)
				? e
				: undefined;
			const label = linkEvent?.detail.link?.label || '';

			emitEvent({
				eventCategory: 'footerNav',
				interactionType: 'click',
				eventAction: 'navigation',
				eventLabel: label || '',
				eventValue: {
					component: 'GlobalFooter',
					section: 'footer',
					key: link.__typename === 'FooterLink' ? link?.key : '',
					owner: link.owner,
					relativePath: link.relativePath
				}
			});

			const isCommon = onGlobalClick(e);
			if (!isCommon) return;

			if (link && link?.owner === NAV_OWNER && link.relativePath) {
				e.detail.originalEvent.preventDefault();
				const p = link.relativePath.startsWith('/')
					? link.relativePath
					: `/${link.relativePath}`;
				router.push(genRoute(p));
			}
		},
		[locale, router, onGlobalClick]
	);

	return (
		<>
			<EgsFooter
				domain={serverUrl}
				locale={locale}
				countryCode={country}
				footerLinkOnClick={footerLinkOnClick}
			/>
		</>
	);
};
